<template>
  <div>
    <breadcrumbs v-if="mounted" :items="breadcrumbs" />

    <div class="ml-6">
      <v-row v-if="!flagProductUpdated" class="mt-1">
        <v-col cols="12" class="text-center">
          <v-form ref="productForm" v-model="validForm">
            <v-container>
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                      type="number"
                      v-model="product.product_id"
                      :rules="[rules.required]"
                      label="Barcode"
                      placeholder="Barcode"
                      dense
                      readonly
                      outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                      v-model="product.title"
                      :rules="[rules.required]"
                      label="Product name"
                      placeholder="Title"
                      dense
                      outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-select
                      :items="fks.productTypes"
                      label="Product type"
                      v-model="product.type"
                      :rules="[rules.required]"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-select>
                </v-col>

                <v-col cols="12" md="3">
                  <v-autocomplete
                      :items="fks.manufacturers"
                      label="Manufacturer"
                      v-model="product.manufacturer_fk"
                      item-text="title"
                      item-value="id"
                      outlined
                      dense
                      clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                      type="number"
                      v-model="product.volume"
                      label="Volume (ml)"
                      placeholder="Volume"
                      dense
                      outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                      type="number"
                      v-model="product.weight"
                      label="Weight (gr)"
                      placeholder="Weight"
                      dense
                      outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                      type="number"
                      v-model="product.points"
                      label="Points"
                      placeholder="Points"
                      dense
                      outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-select
                      :items="yesNoOptions"
                      label="Single use"
                      v-model="product.single_use"
                      :rules="[rules.required]"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-select>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                      :items="fks.flavours"
                      label="Flavours"
                      v-model="product.flavours"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                      clearable
                      multiple
                      small-chips
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12" md="6">
                  <v-autocomplete
                      :items="fks.colours"
                      label="Colours"
                      v-model="product.colours"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                      clearable
                      multiple
                      small-chips
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="3">
                  <v-select
                      :items="yesNoOptions"
                      label="Recyclable"
                      v-model="product.recyclable"
                      :rules="[rules.required]"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                      :items="yesNoOptions"
                      label="Sparkling"
                      v-model="product.sparkling"
                      :rules="[rules.required]"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                      :items="yesNoOptions"
                      label="Organic"
                      v-model="product.organic"
                      :rules="[rules.required]"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                      :items="yesNoOptions"
                      label="RVM Recyclable"
                      v-model="product.rvm_recyclable"
                      :rules="[rules.required]"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-select>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="3">
                  <v-autocomplete
                      :items="fks.brands"
                      :rules="[rules.required]"
                      label="Brand"
                      v-model="product.brand_fk"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                      :items="fks.materialTypes"
                      :rules="[rules.required]"
                      label="Primary Material Type"
                      v-model="product.material"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                      :items="fks.materialTypes"
                      label="Secondary Material Type"
                      v-model="product.material2"
                      item-text="title"
                      item-value="id"
                      dense
                      outlined
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-file-input
                      v-model="product.image"
                      accept="image/png, image/jpeg, image/gif"
                      placeholder="Product image"
                      label="Click to select an image"
                      append-icon="mdi-camera"
                      prepend-icon=""
                      hint="700 x 700 | max 10MB"
                      persistent-hint
                      outlined
                      show-size
                      height="150"
                      @change="loadImageThumbnail()"
                  ></v-file-input>
                </v-col>
                <v-col cols="12" md="3">
                  <v-img
                      :src="imageUrl"
                  ></v-img>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-col>
      </v-row>

      <v-row class="mt-1 ml-1">
        <v-col cols="12">
          <v-btn
              v-if="!flagProductUpdated"
              color="success"
              :disabled="flagUpdatingProduct"
              @click="updateProduct()"
              class="mr-2"
          >
            <v-icon class="mr-2">mdi-check</v-icon>
            Proceed
          </v-btn>
          <v-btn
              color="warning"
              @click="$router.push('/vrvm/incomplete-products/view')"
          >
            <v-icon class="mr-2">mdi-chevron-left</v-icon>
            Go back
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <v-snackbar
        v-model="snackbar"
        absolute
        top right
        :color="operationResultColor"
        elevation="24"
    >
      {{ operationResultMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";

export default ({
  name: "FixIncompleteProduct",

  data: () => ({
    validForm: false,
    rules: {
      required: (value) => !!value || "This field is required"
    },
    yesNoOptions: [
      {id: "1", title: "Yes"},
      {id: "0", title: "No"}
    ],
    operationResultMessage: "",
    operationResultColor: "",
    snackbar: false,
    flagProductUpdated: false,
    flagLoadingProducts: false,
    flagUpdatingProduct: false,
    productsHeaders: [
      {text: "Barcode", value: "product_id"},
      {text: "Created at", value: "created_at"},
      {text: "Actions", value: "actions", sortable: false}
    ],
    breadcrumbs: [
      {text: "Incomplete products", disabled: false, href: "/#/vrvm/incomplete-products/view"}
    ],
    initialImageUrl: "data:image/png;base64, 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",
    imageUrl: "",
    mounted: false,
  }),
  props: ["fks", "product"],
  mounted() {
    this.initialImageUrl = process.env.VUE_APP_DEFAULT_EMPTY_IMAGE;
    this.imageUrl = this.initialImageUrl;
    this.breadcrumbs.push({
      text: `Fix Product ${this.product.product_id}`, disabled: true
    });

    this.mounted = true

    this.$refs.productForm.validate()
  },
  methods: {
    loadImageThumbnail() {
      this.imageUrl = (this.product.image)
          ? URL.createObjectURL(this.product.image)
          : this.initialImageUrl;
    },
    updateProduct() {
      if (this.$refs.productForm.validate()) {
        this.flagUpdatingProduct = true;
        let url = `${process.env.VUE_APP_BASE_API}vrvm/incomplete-products/fix`;

        let bodyFormData = new FormData();
        bodyFormData.append('product', JSON.stringify(this.product));
        bodyFormData.append('image', this.product.image);

        axios({
          method: "post",
          url: url,
          data: bodyFormData,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${this.$store.getters.getJwt}`
          },
        })
            .then((response) => {
              this.flagUpdatingProduct = false;
              this.flagProductUpdated = true;

              this.operationResultMessage = response.data;
              this.operationResultColor = "success";
              this.snackbar = true;
            })
            .catch((error) => {
              this.dlgFixProduct = false;
              this.flagUpdatingProduct = false;

              this.snackbar = true;
              this.operationResultMessage = error.data ?? "An error has occurred.";
              this.operationResultColor = "error";
            });
      }
    }
  }
});
</script>

<style scoped>
.v-input {
  font-size: .9em;
}

.v-image {
  border: 1px dashed grey;
  height: 150px;
  width: auto;
}
</style>
